import React, { useState } from 'react';
import Input from '../../../utils/Input';
import Textarea from '../../../utils/Textarea';
import Button from '../../../utils/Button';
import { useApiMutation } from '../../../hooks/useMutation';
import toast from 'react-hot-toast';

interface ContentBlock {
  id: string;
  heading: string;
  body: string;
}

interface PrivacyPolicyData {
  title: string;
  caption: string;
  description: string;
  contentBlocks: ContentBlock[];
}

type ViewMode = 'build' | 'preview';
type DocumentType = 'privacy-policy' | 'terms-platform' | 'terms-website' | 'faq';

// Map frontend document types to backend template types
const documentTypeToTemplateType: Record<DocumentType, string> = {
  'privacy-policy': 'PRIVACY_POLICY',
  'terms-platform': 'TERMS_PLATFORM',
  'terms-website': 'TERMS_WEBSITE',
  'faq': 'FAQ'
};

const documentTypeConfig = {
  'privacy-policy': {
    title: 'Privacy Policy',
    titlePlaceholder: 'Enter privacy policy title',
    captionPlaceholder: 'Enter a brief caption for the privacy policy',
    descriptionPlaceholder: 'Enter the main description for your privacy policy',
    blockHeadingPlaceholder: 'Enter privacy policy section heading',
    blockBodyPlaceholder: 'Enter privacy policy section content'
  },
  'terms-platform': {
    title: 'Terms and Conditions (Platform)',
    titlePlaceholder: 'Enter platform terms and conditions title',
    captionPlaceholder: 'Enter a brief caption for the platform terms',
    descriptionPlaceholder: 'Enter the main description for your platform terms and conditions',
    blockHeadingPlaceholder: 'Enter terms section heading',
    blockBodyPlaceholder: 'Enter terms section content'
  },
  'terms-website': {
    title: 'Terms and Conditions (Website)',
    titlePlaceholder: 'Enter website terms and conditions title',
    captionPlaceholder: 'Enter a brief caption for the website terms',
    descriptionPlaceholder: 'Enter the main description for your website terms and conditions',
    blockHeadingPlaceholder: 'Enter terms section heading',
    blockBodyPlaceholder: 'Enter terms section content'
  },
  'faq': {
    title: 'Frequently Asked Questions',
    titlePlaceholder: 'Enter FAQ title',
    captionPlaceholder: 'Enter a brief caption for the FAQ',
    descriptionPlaceholder: 'Enter the main description for your FAQ',
    blockHeadingPlaceholder: 'Enter question',
    blockBodyPlaceholder: 'Enter answer'
  }
};

const createContentBlockId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `block-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

const renderFormattedText = (text: string) => {
  return text.split('\n').map((line, lineIndex) => {
    const segments = line.split(/(<b>.*?<\/b>)/g);

    return (
      <React.Fragment key={`line-${lineIndex}`}>
        {segments.map((segment, segmentIndex) => {
          const boldMatch = segment.match(/^<b>(.*?)<\/b>$/);

          if (boldMatch) {
            return (
              <strong key={`segment-${lineIndex}-${segmentIndex}`}>
                {boldMatch[1]}
              </strong>
            );
          }

          return (
            <React.Fragment key={`segment-${lineIndex}-${segmentIndex}`}>
              {segment}
            </React.Fragment>
          );
        })}
        {lineIndex < text.split('\n').length - 1 && <br />}
      </React.Fragment>
    );
  });
};

const Templates: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('build');
  const [documentType, setDocumentType] = useState<DocumentType>('privacy-policy');
  const [privacyData, setPrivacyData] = useState<PrivacyPolicyData>({
    title: '',
    caption: '',
    description: '',
    contentBlocks: []
  });

  // Mutation for uploading templates
  const uploadTemplateMutation = useApiMutation({
    onSuccess: () => {
      toast.success('Template uploaded successfully!');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to upload template';
      toast.error(errorMessage);
    }
  });

  const addContentBlock = () => {
    const newBlock: ContentBlock = {
      id: createContentBlockId(),
      heading: '',
      body: ''
    };
    setPrivacyData(prev => ({
      ...prev,
      contentBlocks: [...prev.contentBlocks, newBlock]
    }));
  };

  const updateContentBlock = (id: string, field: 'heading' | 'body', value: string) => {
    setPrivacyData(prev => ({
      ...prev,
      contentBlocks: prev.contentBlocks.map(block =>
        block.id === id ? { ...block, [field]: value } : block
      )
    }));
  };

  const removeContentBlock = (id: string) => {
    setPrivacyData(prev => ({
      ...prev,
      contentBlocks: prev.contentBlocks.filter(block => block.id !== id)
    }));
  };

  const updateBasicInfo = (field: keyof PrivacyPolicyData, value: string) => {
    setPrivacyData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    // Validate required fields
    if (!privacyData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!privacyData.caption.trim()) {
      toast.error('Caption is required');
      return;
    }

    if (!privacyData.description.trim()) {
      toast.error('Description is required');
      return;
    }

    if (privacyData.contentBlocks.length === 0) {
      toast.error('At least one content block is required');
      return;
    }

    // Validate content blocks
    const invalidBlocks = privacyData.contentBlocks.filter(
      block => !block.heading.trim() || !block.body.trim()
    );

    if (invalidBlocks.length > 0) {
      toast.error('All content blocks must have both heading and body content');
      return;
    }

    const templateData = {
      type: documentTypeToTemplateType[documentType],
      title: privacyData.title.trim(),
      caption: privacyData.caption.trim(),
      description: privacyData.description.trim(),
      blocks: privacyData.contentBlocks.map(block => ({
        heading: block.heading.trim(),
        body: block.body.trim()
      }))
    };

    try {
      await uploadTemplateMutation.mutateAsync({
        method: 'POST',
        url: '/admin/templates',
        data: templateData
      });
    } catch (error) {
      console.error('Template upload failed:', error);
    }
  };

  return (
    <div className="space-y-6 text-slate-900">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex flex-col gap-2">
          <select
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value as DocumentType)}
            className="px-4 py-2.5 border border-gray-300 rounded-full bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="privacy-policy">Privacy Policy</option>
            <option value="terms-platform">Terms and Conditions (Platform)</option>
            <option value="terms-website">Terms and Conditions (Website)</option>
            <option value="faq">Frequently Asked Questions</option>
          </select>
          <h2 className="text-2xl font-bold ">{documentTypeConfig[documentType].title}</h2>
        </div>
        <div className="flex gap-2">
          <Button
            label="Build"
            variant={viewMode === 'build' ? 'primary' : 'tertiary'}
            size="medium"
            onClick={() => setViewMode('build')}
          />
          <Button
            label="Preview"
            variant={viewMode === 'preview' ? 'primary' : 'tertiary'}
            size="medium"
            onClick={() => setViewMode('preview')}
          />
        </div>
      </div>

      {viewMode === 'build' ? (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 ">
            <h3 className="text-lg font-semibold  mb-4">Basic Information</h3>
            <div className="space-y-4">
              <Input
                label="Title"
                placeholder={documentTypeConfig[documentType].titlePlaceholder}
                value={privacyData.title}
                onChange={(e) => updateBasicInfo('title', e.target.value)}
                debounce={0}
                color="white"
                size="large"
              />
              
              <Input
                label="Caption for Title"
                placeholder={documentTypeConfig[documentType].captionPlaceholder}
                value={privacyData.caption}
                onChange={(e) => updateBasicInfo('caption', e.target.value)}
                debounce={0}
                color="white"
                size="large"
              />
              
              <Textarea
                label="Description"
                placeholder={documentTypeConfig[documentType].descriptionPlaceholder}
                value={privacyData.description}
                onChange={(e) => updateBasicInfo('description', e.target.value)}
                debounce={0}
                color="white"
                rows={10}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Content Blocks</h3>
              <Button
                label="Add New Block"
                variant="primary"
                size="medium"
                onClick={addContentBlock}
              />
            </div>

            {privacyData.contentBlocks.length === 0 ? (
              <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-600 rounded-lg">
                <p className="mb-2">No content blocks yet</p>
                <p className="text-sm">Click "Add New Block" to create your first content block</p>
              </div>
            ) : (
              <div className="space-y-4 ">
                {privacyData.contentBlocks.map((block, index) => (
                  <div key={block.id} className="text-black border-b-2 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-slate-900 font-medium">Block {index + 1}</h4>
                      <Button
                        label="Remove"
                        variant="secondary"
                        size="small"
                        onClick={() => removeContentBlock(block.id)}
                      />
                    </div>
                    <div className="space-y-3">
                      <Input
                        label="Heading"
                        placeholder={documentTypeConfig[documentType].blockHeadingPlaceholder}
                        value={block.heading}
                        onChange={(e) => updateContentBlock(block.id, 'heading', e.target.value)}
                        debounce={0}
                        color="white"
                        size="medium"
                      />
                      <Textarea
                        label="Body"
                        placeholder={documentTypeConfig[documentType].blockBodyPlaceholder}
                        value={block.body}
                        onChange={(e) => updateContentBlock(block.id, 'body', e.target.value)}
                        debounce={0}
                        color="white"
                        rows={8}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              label={uploadTemplateMutation.isPending ? 'Saving...' : 'Save Changes'}
              variant="primary"
              size="large"
              onClick={handleSave}
              disabled={uploadTemplateMutation.isPending}
            />
          </div>
        </div>
      ) : (
        /* Preview Mode */
        <div className="bg-white rounded-xl p-8 ">
          <div className="   mx-auto">
            {privacyData.title ? (
              <>
                {/* Title Section */}
                <div className="text-left mb-8">
                  <h1 className="text-3xl font-bold text-slate-900 mb-2">{privacyData.title}</h1>
                  {privacyData.caption && (
                    <p className="text-lg text-gray-300">{renderFormattedText(privacyData.caption)}</p>
                  )}
                </div>

                {/* Description */}
                {privacyData.description && (
                  <div className="mb-8">
                    <p className="text-gray-800 leading-relaxed">{renderFormattedText(privacyData.description)}</p>
                  </div>
                )}

                {/* Content Blocks */}
                {privacyData.contentBlocks.length > 0 && (
                  <div className="space-y-6">
                    {privacyData.contentBlocks.map((block) => (
                      <div key={block.id} className="">
                        {block.heading && (
                          <h2 className="text-xl font-semibold text-slate-900 mb-3 border-l-2 border-red-500 bg-red-100 pl-6 py-2.5">{block.heading}</h2>
                        )}
                        {block.body && (
                          <div className="text-black leading-relaxed">
                            {renderFormattedText(block.body)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <p className="text-lg">No content to preview</p>
                <p className="text-sm mt-2">Switch to Build mode to add content</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Templates;
